/**
 * RTK Query Base Query using existing apiClient
 * 
 * This base query leverages the Phase 1 apiClient normalization layer to:
 * 1. Maintain snake_case throughout the stack (no conversion)
 * 2. Handle all API response envelope formats consistently
 * 3. Provide normalized error handling
 * 4. Preserve medical software compliance patterns
 * 
 * By using apiClient, we get all the benefits of Phase 1's anti-corruption
 * layer while enabling RTK Query's modern server state management.
 */

import apiClient from '../../helpers/apiClient';

/**
 * Custom base query that uses the existing apiClient normalization layer
 * @param {Object} args - The query arguments
 * @param {string} args.url - The API endpoint URL
 * @param {string} args.method - HTTP method (GET, POST, etc.)
 * @param {Object} args.data - Request body data (maintains snake_case)
 * @param {Object} args.params - URL parameters
 * @param {Object} args.headers - Additional headers
 * @returns {Object} Normalized response or error
 */
export const apiClientBaseQuery = async ({ url, method = 'GET', data, params, headers = {} }) => {
  try {
    // Use apiClient which already handles:
    // - JWT token injection
    // - Response envelope normalization  
    // - snake_case preservation
    // - Error standardization
    const response = await apiClient({
      url,
      method,
      data,
      params,
      headers,
    });

    // apiClient returns { data, metadata, error, success }
    if (response.error) {
      return {
        error: {
          status: response.error.status,
          data: response.error,
        },
      };
    }

    // For RTK Query, we should return the apiClient response directly
    // RTK Query expects { data: actualData } not { data: { data: actualData } }
    return {
      data: response,
    };
  } catch (error) {
    // Fallback error handling (shouldn't reach here due to apiClient's error handling)
    return {
      error: {
        status: error.response?.status || 500,
        data: {
          message: error.message || 'Unknown error occurred',
          code: 'NETWORK_ERROR',
        },
      },
    };
  }
};

export default apiClientBaseQuery;